"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useId, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { cn } from "../../../utils";
import type { VariantProps, SvgProps } from "../../color-block.types";

// Matches the SVG's base <rect> fill so any sub-pixel rendering gap
// at the edges shows the colour, not the white page background.
const BG_CLASS: Record<NonNullable<VariantProps["color"]>, string> = {
  "bright-blue": "bg-[#5375CF]",
  "light-green": "bg-[#9BD7D2]",
  turquoise: "bg-[#00968C]",
  peach: "bg-[#FFB497]",
  avocado: "bg-[#CCE88A]",
  "sky-blue": "bg-[#A6B9F1]",
  orange: "bg-[#F0B464]",
  sea: "bg-[#007190]",
  mint: "bg-[#86CD9F]",
  yellow: "bg-[#FFD152]",
  cobalt: "bg-[#2B52B8]",
  ocean: "bg-[#1A3C82]"
};
function RectangleSmSvg(t0) {
  const $ = _c(195);
  const {
    color,
    animated,
    shapeScale
  } = t0;
  const id = useId();
  let t1;
  if ($[0] !== animated || $[1] !== shapeScale) {
    t1 = origin => ({
      scale: animated ? shapeScale : 1,
      transformOrigin: origin
    });
    $[0] = animated;
    $[1] = shapeScale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const style = t1;
  switch (color) {
    case "bright-blue":
      {
        const t2 = `${id}-bright-blue`;
        let t3;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[3] = t3;
        } else {
          t3 = $[3];
        }
        let t4;
        if ($[4] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[4] = t2;
          $[5] = t4;
        } else {
          t4 = $[5];
        }
        const t5 = `url(#${id}-bright-blue)`;
        let t6;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#5375CF" />;
          $[6] = t6;
        } else {
          t6 = $[6];
        }
        let t7;
        if ($[7] !== style) {
          t7 = <motion.path opacity={0.4} d="M1440 827H799V506.959C799 330.211 942.517 187 1119.46 187C1296.4 187 1440 330.293 1440 506.959V827Z" fill="#4668C2" style={style("bottom right")} />;
          $[7] = style;
          $[8] = t7;
        } else {
          t7 = $[8];
        }
        let t8;
        if ($[9] !== style) {
          t8 = <motion.path opacity={0.7} d="M479 -391L1120 -391L1120 -70.4588C1120 106.565 976.483 250 799.541 250C622.6 250 479 106.483 479 -70.4587L479 -391Z" fill="#4668C2" style={style("top center")} />;
          $[9] = style;
          $[10] = t8;
        } else {
          t8 = $[10];
        }
        let t9;
        if ($[11] !== t5 || $[12] !== t7 || $[13] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[11] = t5;
          $[12] = t7;
          $[13] = t8;
          $[14] = t9;
        } else {
          t9 = $[14];
        }
        let t10;
        if ($[15] !== t4 || $[16] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[15] = t4;
          $[16] = t9;
          $[17] = t10;
        } else {
          t10 = $[17];
        }
        return t10;
      }
    case "light-green":
      {
        const t2 = `${id}-light-green`;
        let t3;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[18] = t3;
        } else {
          t3 = $[18];
        }
        let t4;
        if ($[19] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[19] = t2;
          $[20] = t4;
        } else {
          t4 = $[20];
        }
        const t5 = `url(#${id}-light-green)`;
        let t6;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#9BD7D2" />;
          $[21] = t6;
        } else {
          t6 = $[21];
        }
        let t7;
        if ($[22] !== style) {
          t7 = <motion.path opacity={0.4} d="M1440 531H879V250.464C879 95.5334 1004.61 -30 1159.46 -30C1314.32 -30 1440 95.6056 1440 250.464V531Z" fill="#85CDC7" style={style("bottom right")} />;
          $[22] = style;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        let t8;
        if ($[24] !== style) {
          t8 = <motion.path opacity={0.7} d="M318 -30L879 -30L879 250.536C879 405.467 753.394 531 598.536 531C443.678 531 318 405.394 318 250.536L318 -30Z" fill="#85CDC7" style={style("center left")} />;
          $[24] = style;
          $[25] = t8;
        } else {
          t8 = $[25];
        }
        let t9;
        if ($[26] !== t5 || $[27] !== t7 || $[28] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[26] = t5;
          $[27] = t7;
          $[28] = t8;
          $[29] = t9;
        } else {
          t9 = $[29];
        }
        let t10;
        if ($[30] !== t4 || $[31] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[30] = t4;
          $[31] = t9;
          $[32] = t10;
        } else {
          t10 = $[32];
        }
        return t10;
      }
    case "turquoise":
      {
        const t2 = `${id}-turquoise`;
        let t3;
        if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[33] = t3;
        } else {
          t3 = $[33];
        }
        let t4;
        if ($[34] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[34] = t2;
          $[35] = t4;
        } else {
          t4 = $[35];
        }
        const t5 = `url(#${id}-turquoise)`;
        let t6;
        if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#00968C" />;
          $[36] = t6;
        } else {
          t6 = $[36];
        }
        let t7;
        if ($[37] !== style) {
          t7 = <motion.circle cx="871.5" cy="486.5" r="236.5" fill="#00A399" style={style("bottom center")} />;
          $[37] = style;
          $[38] = t7;
        } else {
          t7 = $[38];
        }
        let t8;
        if ($[39] !== style) {
          t8 = <motion.rect opacity={0.65} x="635" y="-223" width="473" height="473" rx="40" fill="#00A399" style={style("top center")} />;
          $[39] = style;
          $[40] = t8;
        } else {
          t8 = $[40];
        }
        let t9;
        if ($[41] !== style) {
          t9 = <motion.circle cx="1344.5" cy="13.5" r="236.5" fill="#00A399" style={style("top right")} />;
          $[41] = style;
          $[42] = t9;
        } else {
          t9 = $[42];
        }
        let t10;
        if ($[43] !== style) {
          t10 = <motion.rect opacity={0.65} x="1108" y="250" width="473" height="473" rx="40" fill="#00A399" style={style("bottom right")} />;
          $[43] = style;
          $[44] = t10;
        } else {
          t10 = $[44];
        }
        let t11;
        if ($[45] !== t10 || $[46] !== t5 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
          t11 = <g clipPath={t5}>{t6}{t7}{t8}{t9}{t10}</g>;
          $[45] = t10;
          $[46] = t5;
          $[47] = t7;
          $[48] = t8;
          $[49] = t9;
          $[50] = t11;
        } else {
          t11 = $[50];
        }
        let t12;
        if ($[51] !== t11 || $[52] !== t4) {
          t12 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t11}</svg>;
          $[51] = t11;
          $[52] = t4;
          $[53] = t12;
        } else {
          t12 = $[53];
        }
        return t12;
      }
    case "peach":
      {
        const t2 = `${id}-peach`;
        let t3;
        if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[54] = t3;
        } else {
          t3 = $[54];
        }
        let t4;
        if ($[55] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[55] = t2;
          $[56] = t4;
        } else {
          t4 = $[56];
        }
        const t5 = `url(#${id}-peach)`;
        let t6;
        if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#FFB497" />;
          $[57] = t6;
        } else {
          t6 = $[57];
        }
        let t7;
        if ($[58] !== style) {
          t7 = <motion.path opacity={0.7} d="M891.07 527H578V213.97C577.928 79.2199 687.162 -30 821.93 -30H1135V283.03C1135 417.78 1025.77 527 890.999 527" fill="#FFAF8F" style={style("center center")} />;
          $[58] = style;
          $[59] = t7;
        } else {
          t7 = $[59];
        }
        let t8;
        if ($[60] !== style) {
          t8 = <motion.path opacity={0.7} d="M1448.07 530H1135V216.97C1134.93 82.2199 1244.16 -27 1378.93 -27H1692V286.03C1692 420.78 1582.77 530 1448 530" fill="#F8A98A" style={style("center right")} />;
          $[60] = style;
          $[61] = t8;
        } else {
          t8 = $[61];
        }
        let t9;
        if ($[62] !== t5 || $[63] !== t7 || $[64] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[62] = t5;
          $[63] = t7;
          $[64] = t8;
          $[65] = t9;
        } else {
          t9 = $[65];
        }
        let t10;
        if ($[66] !== t4 || $[67] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[66] = t4;
          $[67] = t9;
          $[68] = t10;
        } else {
          t10 = $[68];
        }
        return t10;
      }
    case "avocado":
      {
        const t2 = `${id}-avocado`;
        let t3;
        if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[69] = t3;
        } else {
          t3 = $[69];
        }
        let t4;
        if ($[70] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[70] = t2;
          $[71] = t4;
        } else {
          t4 = $[71];
        }
        const t5 = `url(#${id}-avocado)`;
        let t6;
        if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#CCE88A" />;
          $[72] = t6;
        } else {
          t6 = $[72];
        }
        let t7;
        if ($[73] !== style) {
          t7 = <motion.path d="M1221.5 -107.011L854 260.489L1221.5 627.989L1589 260.489L1221.5 -107.011Z" fill="#C2E079" style={style("center right")} />;
          $[73] = style;
          $[74] = t7;
        } else {
          t7 = $[74];
        }
        let t8;
        if ($[75] !== style) {
          t8 = <motion.path d="M854.5 -107.011L487 260.489L854.5 627.989L1222 260.489L854.5 -107.011Z" fill="#C6E57D" style={style("center center")} />;
          $[75] = style;
          $[76] = t8;
        } else {
          t8 = $[76];
        }
        let t9;
        if ($[77] !== t5 || $[78] !== t7 || $[79] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[77] = t5;
          $[78] = t7;
          $[79] = t8;
          $[80] = t9;
        } else {
          t9 = $[80];
        }
        let t10;
        if ($[81] !== t4 || $[82] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[81] = t4;
          $[82] = t9;
          $[83] = t10;
        } else {
          t10 = $[83];
        }
        return t10;
      }
    case "sky-blue":
      {
        const t2 = `${id}-sky-blue`;
        let t3;
        if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[84] = t3;
        } else {
          t3 = $[84];
        }
        let t4;
        if ($[85] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[85] = t2;
          $[86] = t4;
        } else {
          t4 = $[86];
        }
        const t5 = `url(#${id}-sky-blue)`;
        let t6;
        if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#A6B9F1" />;
          $[87] = t6;
        } else {
          t6 = $[87];
        }
        let t7;
        if ($[88] !== style) {
          t7 = <motion.path d="M891.07 527H578V213.97C577.928 79.2199 687.162 -30 821.93 -30H1135V283.03C1135 417.78 1025.77 527 890.999 527" fill="#9BB1EF" style={style("center center")} />;
          $[88] = style;
          $[89] = t7;
        } else {
          t7 = $[89];
        }
        let t8;
        if ($[90] !== style) {
          t8 = <motion.path d="M1448.07 530H1135V216.97C1134.93 82.2199 1244.16 -27 1378.93 -27H1692V286.03C1692 420.78 1582.77 530 1448 530" fill="#90A7E7" style={style("center right")} />;
          $[90] = style;
          $[91] = t8;
        } else {
          t8 = $[91];
        }
        let t9;
        if ($[92] !== t5 || $[93] !== t7 || $[94] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[92] = t5;
          $[93] = t7;
          $[94] = t8;
          $[95] = t9;
        } else {
          t9 = $[95];
        }
        let t10;
        if ($[96] !== t4 || $[97] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[96] = t4;
          $[97] = t9;
          $[98] = t10;
        } else {
          t10 = $[98];
        }
        return t10;
      }
    case "orange":
      {
        const t2 = `${id}-orange`;
        let t3;
        if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[99] = t3;
        } else {
          t3 = $[99];
        }
        let t4;
        if ($[100] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[100] = t2;
          $[101] = t4;
        } else {
          t4 = $[101];
        }
        const t5 = `url(#${id}-orange)`;
        let t6;
        if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#F0B464" />;
          $[102] = t6;
        } else {
          t6 = $[102];
        }
        let t7;
        if ($[103] !== style) {
          t7 = <motion.path opacity={0.5} d="M1440 819C1440 823.418 1436.42 827 1432 827H807C802.582 827 799 823.418 799 819V506.959C799 330.211 942.517 187 1119.46 187C1296.4 187 1440 330.293 1440 506.959V819Z" fill="#EFAC53" style={style("bottom right")} />;
          $[103] = style;
          $[104] = t7;
        } else {
          t7 = $[104];
        }
        let t8;
        if ($[105] !== style) {
          t8 = <motion.path d="M479 -383C479 -387.418 482.582 -391 487 -391L1112 -391C1116.42 -391 1120 -387.418 1120 -383L1120 -70.4588C1120 106.565 976.483 250 799.541 250C622.6 250 479 106.483 479 -70.4587L479 -383Z" fill="#EFAC53" style={style("top center")} />;
          $[105] = style;
          $[106] = t8;
        } else {
          t8 = $[106];
        }
        let t9;
        if ($[107] !== t5 || $[108] !== t7 || $[109] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[107] = t5;
          $[108] = t7;
          $[109] = t8;
          $[110] = t9;
        } else {
          t9 = $[110];
        }
        let t10;
        if ($[111] !== t4 || $[112] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[111] = t4;
          $[112] = t9;
          $[113] = t10;
        } else {
          t10 = $[113];
        }
        return t10;
      }
    case "sea":
      {
        const t2 = `${id}-sea`;
        let t3;
        if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[114] = t3;
        } else {
          t3 = $[114];
        }
        let t4;
        if ($[115] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[115] = t2;
          $[116] = t4;
        } else {
          t4 = $[116];
        }
        const t5 = `url(#${id}-sea)`;
        let t6;
        if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#007190" />;
          $[117] = t6;
        } else {
          t6 = $[117];
        }
        let t7;
        if ($[118] !== style) {
          t7 = <motion.path opacity={0.4} d="M1440 531H879V250.464C879 95.5334 1004.61 -30 1159.46 -30C1314.32 -30 1440 95.6056 1440 250.464V531Z" fill="#008AAA" style={style("bottom right")} />;
          $[118] = style;
          $[119] = t7;
        } else {
          t7 = $[119];
        }
        let t8;
        if ($[120] !== style) {
          t8 = <motion.path opacity={0.7} d="M318 -30L879 -30L879 250.536C879 405.467 753.394 531 598.536 531C443.678 531 318 405.394 318 250.536L318 -30Z" fill="#008AAA" style={style("center left")} />;
          $[120] = style;
          $[121] = t8;
        } else {
          t8 = $[121];
        }
        let t9;
        if ($[122] !== t5 || $[123] !== t7 || $[124] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[122] = t5;
          $[123] = t7;
          $[124] = t8;
          $[125] = t9;
        } else {
          t9 = $[125];
        }
        let t10;
        if ($[126] !== t4 || $[127] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[126] = t4;
          $[127] = t9;
          $[128] = t10;
        } else {
          t10 = $[128];
        }
        return t10;
      }
    case "mint":
      {
        const t2 = `${id}-mint`;
        let t3;
        if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[129] = t3;
        } else {
          t3 = $[129];
        }
        let t4;
        if ($[130] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[130] = t2;
          $[131] = t4;
        } else {
          t4 = $[131];
        }
        const t5 = `url(#${id}-mint)`;
        let t6;
        if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#86CD9F" />;
          $[132] = t6;
        } else {
          t6 = $[132];
        }
        let t7;
        if ($[133] !== style) {
          t7 = <motion.path d="M891.07 527H578V213.97C577.928 79.2199 687.162 -30 821.93 -30H1135V283.03C1135 417.78 1025.77 527 890.999 527" fill="#77C793" style={style("center center")} />;
          $[133] = style;
          $[134] = t7;
        } else {
          t7 = $[134];
        }
        let t8;
        if ($[135] !== style) {
          t8 = <motion.path d="M1448.07 530H1135V216.97C1134.93 82.2199 1244.16 -27 1378.93 -27H1692V286.03C1692 420.78 1582.77 530 1448 530" fill="#77C793" style={style("center right")} />;
          $[135] = style;
          $[136] = t8;
        } else {
          t8 = $[136];
        }
        let t9;
        if ($[137] !== t5 || $[138] !== t7 || $[139] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[137] = t5;
          $[138] = t7;
          $[139] = t8;
          $[140] = t9;
        } else {
          t9 = $[140];
        }
        let t10;
        if ($[141] !== t4 || $[142] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[141] = t4;
          $[142] = t9;
          $[143] = t10;
        } else {
          t10 = $[143];
        }
        return t10;
      }
    case "yellow":
      {
        const t2 = `${id}-yellow`;
        let t3;
        if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[144] = t3;
        } else {
          t3 = $[144];
        }
        let t4;
        if ($[145] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[145] = t2;
          $[146] = t4;
        } else {
          t4 = $[146];
        }
        const t5 = `url(#${id}-yellow)`;
        let t6;
        if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#FFD152" />;
          $[147] = t6;
        } else {
          t6 = $[147];
        }
        let t7;
        if ($[148] !== style) {
          t7 = <motion.path d="M1199.28 -143L805.566 250.141L1199.28 643.283L1593 250.141L1199.28 -143Z" fill="#FFC627" style={style("center right")} />;
          $[148] = style;
          $[149] = t7;
        } else {
          t7 = $[149];
        }
        let t8;
        if ($[150] !== style) {
          t8 = <motion.path d="M706.717 -143L313 250.141L706.717 643.283L1100.43 250.141L706.717 -143Z" fill="#FFCC3D" style={style("center center")} />;
          $[150] = style;
          $[151] = t8;
        } else {
          t8 = $[151];
        }
        let t9;
        if ($[152] !== t5 || $[153] !== t7 || $[154] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[152] = t5;
          $[153] = t7;
          $[154] = t8;
          $[155] = t9;
        } else {
          t9 = $[155];
        }
        let t10;
        if ($[156] !== t4 || $[157] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[156] = t4;
          $[157] = t9;
          $[158] = t10;
        } else {
          t10 = $[158];
        }
        return t10;
      }
    case "cobalt":
      {
        const t2 = `${id}-cobalt`;
        let t3;
        if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[159] = t3;
        } else {
          t3 = $[159];
        }
        let t4;
        if ($[160] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[160] = t2;
          $[161] = t4;
        } else {
          t4 = $[161];
        }
        const t5 = `url(#${id}-cobalt)`;
        let t6;
        if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#2B52B8" />;
          $[162] = t6;
        } else {
          t6 = $[162];
        }
        let t7;
        if ($[163] !== style) {
          t7 = <motion.path opacity={0.4} d="M1440 827H799V506.959C799 330.211 942.517 187 1119.46 187C1296.4 187 1440 330.293 1440 506.959V827Z" fill="#2F5FD6" style={style("bottom right")} />;
          $[163] = style;
          $[164] = t7;
        } else {
          t7 = $[164];
        }
        let t8;
        if ($[165] !== style) {
          t8 = <motion.path opacity={0.7} d="M479 -391L1120 -391L1120 -70.4588C1120 106.565 976.483 250 799.541 250C622.6 250 479 106.483 479 -70.4587L479 -391Z" fill="#2F5FD6" style={style("top center")} />;
          $[165] = style;
          $[166] = t8;
        } else {
          t8 = $[166];
        }
        let t9;
        if ($[167] !== t5 || $[168] !== t7 || $[169] !== t8) {
          t9 = <g clipPath={t5}>{t6}{t7}{t8}</g>;
          $[167] = t5;
          $[168] = t7;
          $[169] = t8;
          $[170] = t9;
        } else {
          t9 = $[170];
        }
        let t10;
        if ($[171] !== t4 || $[172] !== t9) {
          t10 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t9}</svg>;
          $[171] = t4;
          $[172] = t9;
          $[173] = t10;
        } else {
          t10 = $[173];
        }
        return t10;
      }
    case "ocean":
      {
        const t2 = `${id}-ocean`;
        let t3;
        if ($[174] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <rect width="1440" height="500" fill="white" />;
          $[174] = t3;
        } else {
          t3 = $[174];
        }
        let t4;
        if ($[175] !== t2) {
          t4 = <defs><clipPath id={t2}>{t3}</clipPath></defs>;
          $[175] = t2;
          $[176] = t4;
        } else {
          t4 = $[176];
        }
        const t5 = `url(#${id}-ocean)`;
        let t6;
        if ($[177] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <rect width="1440" height="500" fill="#1A3C82" />;
          $[177] = t6;
        } else {
          t6 = $[177];
        }
        let t7;
        if ($[178] !== style) {
          t7 = <motion.circle opacity={0.34} cx="871.5" cy="486.5" r="236.5" fill="#4264B8" style={style("bottom center")} />;
          $[178] = style;
          $[179] = t7;
        } else {
          t7 = $[179];
        }
        let t8;
        if ($[180] !== style) {
          t8 = <motion.rect x="635" y="-223" width="473" height="473" rx="40" fill="#3254A2" style={style("top center")} />;
          $[180] = style;
          $[181] = t8;
        } else {
          t8 = $[181];
        }
        let t9;
        if ($[182] !== style) {
          t9 = <motion.circle cx="1344.5" cy="13.5" r="236.5" fill="#284A94" style={style("top right")} />;
          $[182] = style;
          $[183] = t9;
        } else {
          t9 = $[183];
        }
        let t10;
        if ($[184] !== style) {
          t10 = <motion.rect x="1108" y="250" width="473" height="473" rx="40" fill="#3254A2" style={style("bottom right")} />;
          $[184] = style;
          $[185] = t10;
        } else {
          t10 = $[185];
        }
        let t11;
        if ($[186] !== t10 || $[187] !== t5 || $[188] !== t7 || $[189] !== t8 || $[190] !== t9) {
          t11 = <g clipPath={t5}>{t6}{t7}{t8}{t9}{t10}</g>;
          $[186] = t10;
          $[187] = t5;
          $[188] = t7;
          $[189] = t8;
          $[190] = t9;
          $[191] = t11;
        } else {
          t11 = $[191];
        }
        let t12;
        if ($[192] !== t11 || $[193] !== t4) {
          t12 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">{t4}{t11}</svg>;
          $[192] = t11;
          $[193] = t4;
          $[194] = t12;
        } else {
          t12 = $[194];
        }
        return t12;
      }
    default:
      {
        return null;
      }
  }
}
const RectangleSmColorBlock = React.forwardRef<HTMLDivElement, VariantProps>((t0, ref) => {
  const $ = _c(27);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      color: t1,
      animated: t2,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const color = t1 === undefined ? "bright-blue" : t1;
  const animated = t2 === undefined ? true : t2;
  const containerRef = useRef(null);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      target: containerRef,
      offset: ["start end", "end start"]
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    scrollYProgress
  } = useScroll(t3);
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, 1];
    t5 = [1, 1.3];
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  const shapeScale = useTransform(scrollYProgress, t4, t5);
  let t6;
  if ($[9] !== ref) {
    t6 = node => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[9] = ref;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = BG_CLASS[color];
  const t8 = !children && "aspect-[72/25]";
  let t9;
  if ($[11] !== className || $[12] !== t7 || $[13] !== t8) {
    t9 = cn("relative overflow-hidden", t7, t8, className);
    $[11] = className;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== animated || $[16] !== color || $[17] !== shapeScale) {
    t10 = <RectangleSmSvg color={color} animated={animated} shapeScale={shapeScale} />;
    $[15] = animated;
    $[16] = color;
    $[17] = shapeScale;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== children) {
    t11 = children && <div className="relative z-10">{children}</div>;
    $[19] = children;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== props || $[22] !== t10 || $[23] !== t11 || $[24] !== t6 || $[25] !== t9) {
    t12 = <div ref={t6} data-slot="color-block" className={t9} {...props}>{t10}{t11}</div>;
    $[21] = props;
    $[22] = t10;
    $[23] = t11;
    $[24] = t6;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
});
RectangleSmColorBlock.displayName = "RectangleSmColorBlock";
export { RectangleSmColorBlock };